/**
 * ConversationItem — linha da lista de conversas.
 *
 * Hierarquia: o serviço é o identificador principal (o nome do prestador se
 * repete em dezenas de conversas). Preview usa o último recado humano, não o
 * log automático do serviço, e nunca mostra emoji — sempre ícone.
 */
import { useState } from "react";
import {
  Archive,
  ArchiveRestore,
  AtSign,
  Bell,
  BellOff,
  MailOpen,
  MessageCircle,
  MoreVertical,
  Pencil,
  Pin,
  PinOff,
  Users,
  Wrench,
} from "lucide-react";
import IconTip from "../IconTip";
import { STATUS_OPERACIONAL_MAP } from "../../pages/services/helpers";
import { lucideByName } from "../../utils/lucideCatalog";
import { parseSystemMessage } from "./messageIcons";
import { groupColors, groupIcon } from "./groupIcons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

export function timeAgo(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "agora";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    const days = Math.floor(diff / 86400);
    if (days < 7) return `${days}d`;
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
  } catch {
    return "";
  }
}

/** Título: protocolo do serviço quando existe, senão quem está do outro lado. */
export function conversationTitle(t, { internal } = {}) {
  if (t.scope === "service" && t.service_protocolo) return t.service_protocolo;
  if (internal) {
    if (t.title) return t.title;
    if (t.scope === "service") return "Conversa da equipe";
    return (t.other_names || []).join(", ") || "Conversa";
  }
  return t.counterpart_name || "Conversa";
}

export function conversationSubtitle(t, { internal } = {}) {
  if (internal) {
    if (t.scope === "service") return (t.participants || []).map((p) => p.name).join(" · ");
    return t.is_group ? `${(t.participants || []).length} pessoas` : "Conversa direta";
  }
  const parts = [t.counterpart_name];
  if (t.scope === "service" && t.service_client_name) parts.push(t.service_client_name);
  return parts.filter(Boolean).join(" · ");
}

/** Preview: recado humano quando houver; log do serviço fica só como fallback. */
export function conversationPreview(t) {
  const human = t.last_human_preview || (t.unread ? t.last_message_preview : null);
  if (human) return { text: human, system: false };
  if (t.last_message_preview) return { text: t.last_message_preview, system: true };
  return { text: "Sem mensagens ainda", system: true };
}

export function isClosedThread(t) {
  return (
    t.status === "closed" ||
    t.status === "archived" ||
    t.service_status === "finalizado" ||
    t.service_status === "cancelado"
  );
}

/** Ícone da conversa: usa o ícone do Tipo de Serviço quando é conversa de OS. */
export function threadIcon(t, internal) {
  if (t.scope === "service") {
    return lucideByName(t.service_type_icon) || Wrench;
  }
  if (internal) return t.is_group ? Users : MessageCircle;
  return MessageCircle;
}

/**
 * Avatar da conversa: foto do profissional (selfie do app) quando houver,
 * senão o ícone atual. Qualquer erro no carregamento cai no ícone.
 */
export function ThreadAvatar({ photoUrl, icon: Icon, colors, size, iconSize, testId }) {
  const [erro, setErro] = useState(false);
  if (photoUrl && !erro) {
    return (
      <img
        src={photoUrl}
        alt=""
        onError={() => setErro(true)}
        className={`${size} rounded-full object-cover shrink-0 bg-gray-100`}
        data-testid={testId}
      />
    );
  }
  return (
    <span
      className={`${size} rounded-lg flex items-center justify-center shrink-0`}
      style={{ backgroundColor: colors.bg }}
    >
      <Icon size={iconSize} style={{ color: colors.fg }} />
    </span>
  );
}

export default function ConversationItem({
  thread: t,
  active,
  internal,
  photoUrl,
  onClick,
  pref = {},
  onTogglePin,
  onToggleMute,
  onMarkUnread,
  onToggleClosed,
  onEditGroup,
}) {
  const title = conversationTitle(t, { internal });
  const subtitle = conversationSubtitle(t, { internal });
  const preview = conversationPreview(t);
  const parsedPreview = parseSystemMessage(preview.text);
  const PreviewIcon = parsedPreview.hasIcon ? parsedPreview.Icon : null;
  const unread = internal ? t.unread : t.unread_admin;
  const mentioned = internal && (t.mention_unread || 0) > 0;
  const status = t.service_status ? STATUS_OPERACIONAL_MAP[t.service_status] : null;
  const statusLabel = t.service_status === "cancelado" ? "Cancelado" : status?.label;
  const Icon = t.icon && t.is_group ? groupIcon(t.icon) : threadIcon(t, internal);
  const badge = t.is_group ? groupColors(t.color) : groupColors("accent");
  const highlight = active || mentioned;

  return (
    <div className="relative group border-b border-gray-100">
      <button
        onClick={onClick}
        className={`w-full text-left pl-3 pr-9 py-2.5 flex items-center gap-3 transition-colors ${
          highlight ? "bg-[var(--accent-light)]" : "hover:bg-white"
        }`}
        style={
          highlight
            ? { boxShadow: `inset ${mentioned ? 3 : 2}px 0 0 0 var(--accent)` }
            : undefined
        }
        data-testid={`chat-thread-item-${t.id}`}
      >
        <ThreadAvatar
          key={photoUrl || "icon"}
          photoUrl={photoUrl}
          icon={Icon}
          colors={badge}
          size="h-8 w-8"
          iconSize={14}
          testId={`chat-thread-photo-${t.id}`}
        />

        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-1.5">
            <span className="text-[13px] font-semibold text-gray-900 truncate max-w-[46%]">
              {title}
            </span>
            {statusLabel && (
              <span
                className="text-[9px] px-1.5 py-[1px] rounded border font-medium shrink-0 bg-gray-100 text-gray-600 border-gray-200"
                data-testid={`chat-thread-status-${t.id}`}
              >
                {statusLabel}
              </span>
            )}
            {mentioned && (
              <span
                className="text-[9px] px-1.5 py-[1px] rounded-full font-bold text-white shrink-0 inline-flex items-center gap-0.5"
                style={{ backgroundColor: "var(--accent)" }}
                data-testid={`chat-thread-mention-${t.id}`}
              >
                <AtSign size={8} strokeWidth={3} />
                você
              </span>
            )}
            {pref.pinned && (
              <Pin
                size={10}
                className="shrink-0 text-gray-400"
                data-testid={`chat-thread-pinned-${t.id}`}
              />
            )}
            {pref.muted && (
              <BellOff
                size={10}
                className="shrink-0 text-gray-400"
                data-testid={`chat-thread-muted-${t.id}`}
              />
            )}
            {t.status === "closed" && (
              <Archive
                size={10}
                className="shrink-0 text-gray-400"
                data-testid={`chat-thread-closed-${t.id}`}
              />
            )}
            {t.status === "archived" && (
              <span
                className="text-[9px] px-1.5 py-[1px] rounded border font-medium shrink-0 bg-gray-100 text-gray-500 border-gray-200"
                title="Grupo sem participantes ativos"
                data-testid={`chat-thread-archived-${t.id}`}
              >
                Arquivado
              </span>
            )}
            <span className="text-[10px] text-gray-400 shrink-0 ml-auto">
              {timeAgo(t.last_message_at || t.updated_at)}
            </span>
          </span>

          <span className="block text-[11px] text-gray-500 truncate mt-0.5">
            <IconTip
              label={
                internal
                  ? "Pessoas nesta conversa"
                  : "Profissional envolvido no acionamento"
              }
              side="right"
            >
              <span className="">{subtitle}</span>
            </IconTip>
          </span>

          <span className="flex items-center gap-1.5 mt-1">
            {PreviewIcon && (
              <PreviewIcon
                size={11}
                className={`shrink-0 ${preview.system ? "text-gray-300" : "text-gray-400"}`}
              />
            )}
            <span
              className={`text-xs truncate flex-1 ${
                preview.system ? "text-gray-400 italic" : "text-gray-600"
              }`}
            >
              {parsedPreview.text}
            </span>
            {unread > 0 ? (
              <span
                className="shrink-0 min-w-[16px] h-[16px] text-[10px] font-bold rounded-full text-white flex items-center justify-center px-1"
                style={{ backgroundColor: "var(--accent)" }}
                data-testid={`chat-thread-unread-${t.id}`}
              >
                {unread > 9 ? "9+" : unread}
              </span>
            ) : pref.unread_flag ? (
              <span
                className="shrink-0 h-[9px] w-[9px] rounded-full"
                style={{ backgroundColor: "var(--accent)" }}
                title="Marcada como não lida"
                data-testid={`chat-thread-unread-flag-${t.id}`}
              />
            ) : null}
          </span>
        </span>
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="absolute top-2 right-1.5 p-1 rounded text-gray-400 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 hover:bg-gray-100 hover:text-gray-700 transition-opacity"
            title="Opções da conversa"
            data-testid={`chat-thread-menu-${t.id}`}
          >
            <MoreVertical size={14} />
          </button>
        </DropdownMenuTrigger>
        {/* z acima do Sheet do chat global (!z-[1700]) — senão o menu abre atrás dele */}
        <DropdownMenuContent align="end" className="!z-[1800] min-w-[13rem]">
          <DropdownMenuItem
            onClick={() => onTogglePin?.(t.id, !pref.pinned)}
            className="whitespace-nowrap"
            data-testid={`chat-thread-pin-action-${t.id}`}
          >
            {pref.pinned ? <PinOff size={14} /> : <Pin size={14} />}
            {pref.pinned ? "Desafixar" : "Fixar no topo"}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => onToggleMute?.(t.id, !pref.muted)}
            className="whitespace-nowrap"
            data-testid={`chat-thread-mute-action-${t.id}`}
          >
            {pref.muted ? <Bell size={14} /> : <BellOff size={14} />}
            {pref.muted ? "Reativar avisos" : "Silenciar conversa"}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => onMarkUnread?.(t.id)}
            className="whitespace-nowrap"
            data-testid={`chat-thread-unread-action-${t.id}`}
          >
            <MailOpen size={14} />
            Marcar como não lida
          </DropdownMenuItem>
          {t.is_group && t.scope !== "service" && (
            <DropdownMenuItem
              onClick={() => onEditGroup?.(t.id)}
              className="whitespace-nowrap"
              data-testid={`chat-thread-edit-group-action-${t.id}`}
            >
              <Pencil size={14} />
              Editar grupo
            </DropdownMenuItem>
          )}
          {/* Só conversa vinculada a OS pode ser encerrada. Conversa direta com
              o profissional fica sempre aberta (reabrir segue disponível se
              alguma foi encerrada antes desta regra). */}
          {t.status !== "archived" &&
            (internal || t.scope === "service" || t.status === "closed") && (
            <DropdownMenuItem
              onClick={() => onToggleClosed?.(t.id, t.status !== "closed")}
              className="whitespace-nowrap"
              data-testid={`chat-thread-close-action-${t.id}`}
            >
              {t.status === "closed" ? <ArchiveRestore size={14} /> : <Archive size={14} />}
              {t.status === "closed" ? "Reabrir conversa" : "Encerrar conversa"}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
